/**
 * useGuidedTour — pilote driver.js sur un tour du registre.
 *
 * @remarks
 * - Applique `tour.prepare()` (mise en page nécessaire aux ancres), attend un
 *   frame que React ait committé, puis démarre.
 * - **Onglet par étape** (SAAS-952) : certaines ancres vivent dans une vue de
 *   panneau, montée seulement sur son onglet. Leur `activeTab` est appliqué
 *   AVANT le déplacement (`onNextClick`/`onPrevClick`, qui couvrent aussi les
 *   flèches clavier) et avant le `drive()` initial ; driver.js patiente ensuite
 *   `ANCHOR_WAIT_MS` que l'ancre soit montée — une vue de panneau est chargée
 *   en lazy, un frame ne suffit pas au premier affichage.
 * - **Reprise** : chaque tour redémarre à sa dernière étape atteinte
 *   (persistée par tour). L'index est sauvegardé à chaque étape ; s'il atteint
 *   la fin, il est remis à 0 (relancer rejoue le tour complet).
 * - **Sortie** : à la destruction (Terminé / Échap / clic hors bulle),
 *   `activeTourId` repasse à `null` — ce qui éteint le switch header du tour
 *   d'évaluation.
 *
 * Le type de l'instance driver.js n'est pas importé : on ne consomme que
 * `.destroy()` (typé structurellement, évite un `any`). Les callbacks sont typés
 * par inférence depuis la config driver.js.
 *
 * L'instance en cours est un état de module, pas du hook : driver.js n'affiche
 * qu'un tour à la fois, et plusieurs points d'entrée (switch header, menu
 * compte) doivent pouvoir fermer celui de l'autre.
 */

import { useCallback } from 'react';
import { useLayoutStore } from '@/app/stores/use-layout-store';
import { useGuidedTourStore } from '@/app/stores/use-guided-tour-store';
import { getTour } from './tour-registry';
import type { GuidedTourStep, TourId } from './tour-types';

/**
 * Délai laissé à une ancre pour apparaître avant que driver.js n'abandonne
 * l'étape. Couvre le chargement lazy d'une vue de panneau (import + montage).
 */
const ANCHOR_WAIT_MS = 800;

let activeDriver: { destroy: () => void } | null = null;

/**
 * Active l'onglet exigé par l'étape, s'il y en a un. Les vues de panneau ne sont
 * montées que sur l'onglet actif : sans ce basculement, l'ancre n'existe pas.
 */
function applyStepLayout(step: GuidedTourStep | undefined): void {
  if (!step?.activeTab) return;
  const layout = useLayoutStore.getState();
  if (step.activeTab.side === 'right') layout.setRightActiveTab(step.activeTab.id);
  else layout.setLeftActiveTab(step.activeTab.id);
}

/** Attend un frame que React ait committé la mise en page. */
function nextFrame(): Promise<void> {
  return new Promise((resolve) => {
    requestAnimationFrame(() => {
      resolve();
    });
  });
}

export function useGuidedTour() {
  const setActiveTourId = useGuidedTourStore((s) => s.setActiveTourId);
  const setProgress = useGuidedTourStore((s) => s.setProgress);

  const startTour = useCallback(
    async (tourId: TourId) => {
      const tour = getTour(tourId);
      const { steps } = tour;
      if (steps.length === 0) return;

      // Un tour précédent encore ouvert remettrait `activeTourId` à null en se
      // détruisant : on le ferme avant de déclarer le nouveau.
      activeDriver?.destroy();
      setActiveTourId(tourId);

      tour.prepare?.();
      await nextFrame();

      const { driver } = await import('./guided-tour-driver');

      const lastIndex = steps.length - 1;
      // Reprise à la dernière étape atteinte (bornée : les étapes ont pu changer).
      const resumeIndex = Math.min(
        Math.max(0, useGuidedTourStore.getState().progress[tourId] ?? 0),
        lastIndex,
      );
      let reachedEnd = false;

      const instance = driver({
        showProgress: true,
        nextBtnText: 'Suivant',
        prevBtnText: 'Précédent',
        doneBtnText: 'Terminé',
        progressText: '{{current}} sur {{total}}',
        waitForElement: ANCHOR_WAIT_MS,
        stagePadding: 6,
        stageRadius: 8,
        steps: steps.map((step) => ({
          element: step.selector,
          popover: { title: step.title, description: step.description },
          // Une étape optionnelle dont l'ancre n'apparaît pas est sautée plutôt
          // que présentée en bulle centrée sans cible.
          ...(step.optional ? { skipMissingElement: true } : {}),
        })),
        // Surcharger la navigation impose de déplacer soi-même le tour. Ces deux
        // hooks couvrent aussi les flèches clavier (même résolution interne).
        onNextClick: (_element, _step, opts) => {
          const target = (opts.state.activeIndex ?? 0) + 1;
          applyStepLayout(steps[target]);
          opts.driver.moveNext();
        },
        onPrevClick: (_element, _step, opts) => {
          const target = (opts.state.activeIndex ?? 0) - 1;
          applyStepLayout(steps[target]);
          opts.driver.movePrevious();
        },
        onHighlighted: (_element, _step, opts) => {
          const idx = opts.state.activeIndex ?? 0;
          setProgress(tourId, idx);
          if (idx >= lastIndex) reachedEnd = true;
        },
        onDestroyed: () => {
          setActiveTourId(null);
          // Tour terminé → on repart de zéro au prochain déclenchement.
          if (reachedEnd) setProgress(tourId, 0);
        },
      });

      activeDriver = instance;
      // La reprise peut repartir sur une étape à onglet : il doit être actif
      // avant le premier affichage, pas seulement lors d'un « Suivant ».
      applyStepLayout(steps[resumeIndex]);
      instance.drive(resumeIndex);
    },
    [setActiveTourId, setProgress],
  );

  const stopTour = useCallback(() => {
    activeDriver?.destroy();
  }, []);

  return { startTour, stopTour };
}
